import React, { useState, useEffect, useCallback } from 'react';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { BRANDING } from '../config/branding';
import { 
  Building2, Users, CreditCard, TrendingUp, Plus, Search,
  MoreVertical, Eye, Edit, Pause, Play, Trash2, Settings,
  AlertCircle, CheckCircle, Clock, XCircle, ChevronRight,
  RefreshCw, Calendar, DollarSign, Activity, Shield,
  Package, Copy, Zap, Crown, Building, ArrowUpRight, ArrowDownRight,
  FileText, Bell, Filter, LayoutGrid, List, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Status badge mappings
const statusConfig = {
  ACTIVE: { label: 'Actif', variant: 'default', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  SUSPENDED: { label: 'Suspendu', variant: 'destructive', icon: Pause, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  DISABLED: { label: 'Désactivé', variant: 'secondary', icon: XCircle, color: 'bg-stone-100 text-stone-600 border-stone-200' },
  TRIAL: { label: 'Essai', variant: 'outline', icon: Clock, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  EXPIRED: { label: 'Expiré', variant: 'destructive', icon: AlertCircle, color: 'bg-red-100 text-red-700 border-red-200' },
  CANCELLED: { label: 'Annulé', variant: 'secondary', icon: XCircle, color: 'bg-stone-100 text-stone-500 border-stone-200' },
  LIFETIME: { label: 'Lifetime', variant: 'default', icon: Shield, color: 'bg-purple-100 text-purple-700 border-purple-200' }
};

const setupStateConfig = {
  PENDING: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  IN_PROGRESS: { label: 'En cours', color: 'bg-blue-100 text-blue-700' },
  COMPLETED: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700' }
};

// ============ NEW DASHBOARD TAB - COCKPIT STYLE ============
const DashboardTab = ({ data, loading, onRefresh }) => {
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);

  // Fetch alerts
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const { data } = await api.get('/admin/alerts');
        setAlerts(data.alerts || []);
      } catch (error) {
        console.error('Error fetching alerts:', error);
      } finally {
        setLoadingAlerts(false);
      }
    };
    fetchAlerts();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!data) return null;

  // API returns data under 'business' key
  const business = data.business || data;
  const riads = business.riads || { total: 0, active: 0, suspended: 0, disabled: 0, new_this_month: 0 };
  const subscriptions = business.subscriptions || { active: 0, trial: 0, expiring_soon: 0, churned: 0, byPlan: {} };
  const revenue = business.revenue || { monthly: 0, currency: 'MAD' };
  const users = business.users || { total: 0, active: 0 };

  const getSeverityStyle = (severity) => ({
    critical: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-500', text: 'text-red-700' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-500', text: 'text-amber-700' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500', text: 'text-blue-700' }
  }[severity] || { bg: 'bg-stone-50', border: 'border-stone-200', icon: 'text-stone-500', text: 'text-stone-700' });

  return (
    <div className="space-y-8">
      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Riads */}
        <Card className="relative overflow-hidden border-stone-200/60 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500">Total Riads</p>
                <p className="text-3xl font-bold text-stone-800 mt-1">{riads.total}</p>
                <p className="text-xs text-emerald-600 flex items-center gap-1 mt-2">
                  <ArrowUpRight className="w-3 h-3" />
                  +{riads.new_this_month || 0} ce mois
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 to-orange-400" />
        </Card>

        {/* Active Subscriptions */}
        <Card className="relative overflow-hidden border-stone-200/60 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500">Abonnements Actifs</p>
                <p className="text-3xl font-bold text-stone-800 mt-1">{subscriptions.active}</p>
                <p className="text-xs text-stone-500 mt-2">
                  {subscriptions.trial > 0 && <span className="text-blue-600">{subscriptions.trial} en essai</span>}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-teal-400" />
        </Card>

        {/* MRR */}
        <Card className="relative overflow-hidden border-stone-200/60 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500">Revenus Mensuels</p>
                <p className="text-3xl font-bold text-stone-800 mt-1">
                  {(revenue.monthly || 0).toLocaleString()}
                  <span className="text-lg font-normal text-stone-400 ml-1">{revenue.currency || 'MAD'}</span>
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-indigo-400" />
        </Card>

        {/* Riads Status */}
        <Card className="relative overflow-hidden border-stone-200/60 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-stone-500">Santé</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-3xl font-bold text-emerald-600">{riads.active}</span>
                  <span className="text-stone-400">/</span>
                  <span className="text-amber-500 font-semibold">{riads.suspended}</span>
                  <span className="text-stone-400">/</span>
                  <span className="text-stone-400">{riads.disabled}</span>
                </div>
                <p className="text-xs text-stone-400 mt-2">actifs / suspendus / désactivés</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 to-pink-400" />
        </Card>
      </div>

      {/* Smart Alerts Section */}
      {alerts.length > 0 && (
        <Card className={`border-stone-200/60 shadow-sm ${alerts.some(a => a.severity === 'critical') ? 'ring-2 ring-red-200' : ''}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className={`w-5 h-5 ${alerts.some(a => a.severity === 'critical') ? 'text-red-500 animate-pulse' : 'text-amber-500'}`} />
                Alertes ({alerts.length})
              </CardTitle>
              <div className="flex items-center gap-2 text-xs">
                {alerts.filter(a => a.severity === 'critical').length > 0 && (
                  <Badge className="bg-red-100 text-red-700 animate-pulse">{alerts.filter(a => a.severity === 'critical').length} critiques</Badge>
                )}
                {alerts.filter(a => a.severity === 'warning').length > 0 && (
                  <Badge className="bg-amber-100 text-amber-700">{alerts.filter(a => a.severity === 'warning').length} avertissements</Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {alerts.slice(0, 10).map(alert => {
                const style = getSeverityStyle(alert.severity);
                const isCritical = alert.severity === 'critical';
                return (
                  <div 
                    key={alert.id} 
                    className={`p-3 rounded-lg border ${style.bg} ${style.border} flex items-start gap-3 ${isCritical ? 'shadow-sm' : ''} transition-all hover:shadow-md cursor-pointer`}
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className={`flex-shrink-0 mt-0.5 ${style.icon} ${isCritical ? 'animate-pulse' : ''}`}>
                      {alert.severity === 'critical' ? <AlertCircle className="w-4 h-4" /> : 
                       alert.severity === 'warning' ? <Clock className="w-4 h-4" /> : 
                       <Bell className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold text-sm ${style.text}`}>{alert.title}</h4>
                      <p className="text-xs text-stone-600 mt-0.5 line-clamp-2">{alert.message}</p>
                      {alert.account?.name && (
                        <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {alert.account.name}
                        </p>
                      )}
                    </div>
                    {alert.amount && (
                      <div className="text-right flex-shrink-0">
                        <span className={`text-sm font-bold ${style.text}`}>
                          {alert.amount.toLocaleString()} {alert.currency || 'MAD'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-stone-200/60 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-stone-400" />
              Répartition par Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(subscriptions.byPlan || {}).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(subscriptions.byPlan).map(([plan, count]) => {
                  const total = Object.values(subscriptions.byPlan).reduce((a, b) => a + b, 0);
                  const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={plan}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-stone-700">{plan}</span>
                        <span className="text-sm text-stone-500">{count} ({percent}%)</span>
                      </div>
                      <Progress value={percent} className="h-2" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">Aucune donnée</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-stone-200/60 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Actions Rapides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col gap-2 justify-start items-start text-left hover:border-emerald-300 hover:bg-emerald-50/50 transition-all"
                onClick={() => window.location.hash = '#clients'}
                data-testid="quick-action-new-riad"
              >
                <Plus className="w-5 h-5 text-emerald-600" />
                <span className="font-medium">Nouveau Riad</span>
                <span className="text-xs text-stone-400">Créer un compte client</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col gap-2 justify-start items-start text-left hover:border-blue-300 hover:bg-blue-50/50 transition-all"
                onClick={() => window.location.hash = '#billing'}
                data-testid="quick-action-invoices"
              >
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="font-medium">Factures</span>
                <span className="text-xs text-stone-400">Gérer la facturation</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col gap-2 justify-start items-start text-left hover:border-purple-300 hover:bg-purple-50/50 transition-all"
                onClick={() => window.location.hash = '#super-admins'}
                data-testid="quick-action-users"
              >
                <Users className="w-5 h-5 text-purple-600" />
                <span className="font-medium">Super Admins</span>
                <span className="text-xs text-stone-400">Gérer les accès admin</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col gap-2 justify-start items-start text-left hover:border-amber-300 hover:bg-amber-50/50 transition-all" 
                onClick={onRefresh}
                data-testid="quick-action-refresh"
              >
                <RefreshCw className="w-5 h-5 text-amber-600" />
                <span className="font-medium">Actualiser</span>
                <span className="text-xs text-stone-400">Rafraîchir les données</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============ RIADS TAB ============
const RiadsTab = ({ onRiadSelect }) => {
  const [riads, setRiads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const fetchRiads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const { data } = await api.get(`/admin/riads?${params}`);
      setRiads(data.riads);
      setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (error) {
      toast.error('Erreur lors du chargement des riads');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, statusFilter]);

  useEffect(() => {
    fetchRiads();
  }, [fetchRiads]);

  const handleAction = async (action, riadId, data = {}) => {
    try {
      switch (action) {
        case 'suspend':
          await api.post(`/admin/riads/${riadId}/suspend`, data);
          toast.success('Riad suspendu');
          break;
        case 'reactivate':
          await api.post(`/admin/riads/${riadId}/reactivate`, data);
          toast.success('Riad réactivé');
          break;
        case 'disable':
          await api.post(`/admin/riads/${riadId}/disable`, data);
          toast.success('Riad désactivé');
          break;
        case 'delete':
          await api.delete(`/admin/riads/${riadId}`);
          toast.success('Riad supprimé');
          break;
        default:
          break;
      }
      fetchRiads();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un riad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="ACTIVE">Actifs</SelectItem>
            <SelectItem value="SUSPENDED">Suspendus</SelectItem>
            <SelectItem value="DISABLED">Désactivés</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Riad
        </Button>
      </div>

      {/* Riads List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-3">
          {riads.map(riad => {
            const status = statusConfig[riad.status] || statusConfig.ACTIVE;
            const StatusIcon = status.icon;
            const subStatus = riad.subscription ? statusConfig[riad.subscription.status] : null;

            return (
              <Card key={riad.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold truncate">{riad.name}</h3>
                          <Badge className={status.color} variant="outline">
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          <span>{riad.slug}</span>
                          {riad.email && (
                            <>
                              <span>•</span>
                              <span>{riad.email}</span>
                            </>
                          )}
                          <span>•</span>
                          <span>{riad.userCount || 0} utilisateurs</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {riad.subscription && (
                        <div className="text-right hidden sm:block">
                          <Badge variant="outline">{riad.subscription.plan}</Badge>
                          {subStatus && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {subStatus.label}
                              {riad.subscription.end_date && (
                                <> - Expire le {format(new Date(riad.subscription.end_date), 'dd/MM/yyyy')}</>
                              )}
                            </p>
                          )}
                        </div>
                      )}

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onRiadSelect(riad.id)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Voir détails
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onRiadSelect(riad.id, 'edit')}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {riad.status === 'ACTIVE' && (
                            <DropdownMenuItem 
                              onClick={() => handleAction('suspend', riad.id)}
                              className="text-orange-600"
                            >
                              <Pause className="w-4 h-4 mr-2" />
                              Suspendre
                            </DropdownMenuItem>
                          )}
                          {riad.status === 'SUSPENDED' && (
                            <DropdownMenuItem 
                              onClick={() => handleAction('reactivate', riad.id)}
                              className="text-green-600"
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Réactiver
                            </DropdownMenuItem>
                          )}
                          {riad.id !== 1 && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleAction('delete', riad.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {riads.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              Aucun riad trouvé
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2 pt-4">
          <Button 
            variant="outline" 
            size="sm"
            disabled={pagination.page === 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            Précédent
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {pagination.page} / {pagination.pages}
          </span>
          <Button 
            variant="outline" 
            size="sm"
            disabled={pagination.page === pagination.pages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            Suivant
          </Button>
        </div>
      )}

      {/* Create Dialog */}
      <CreateRiadDialog 
        open={showCreateDialog} 
        onOpenChange={setShowCreateDialog}
        onSuccess={() => {
          setShowCreateDialog(false);
          fetchRiads();
        }}
      />
    </div>
  );
};

// ============ CREATE RIAD DIALOG ============
const CreateRiadDialog = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    subdomain: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    currency: 'MAD',
    country: 'MA',
    timezone: 'Africa/Casablanca',
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_password: '',
    plan_id: null,
    plan: 'STANDARD',
    subscription_status: 'ACTIVE',
    monthly_price: 0,
    trial_days: 0
  });

  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/admin/plans?status=ACTIVE');
      setPlans(data.plans);
      // Set default plan
      const defaultPlan = data.plans.find(p => p.is_default) || data.plans[0];
      if (defaultPlan) {
        setFormData(prev => ({
          ...prev,
          plan_id: defaultPlan.id,
          plan: defaultPlan.code.toUpperCase(),
          monthly_price: defaultPlan.price,
          trial_days: defaultPlan.trial_days
        }));
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const handlePlanChange = (planId) => {
    const plan = plans.find(p => p.id === parseInt(planId));
    if (plan) {
      setFormData(prev => ({
        ...prev,
        plan_id: plan.id,
        plan: plan.code.toUpperCase(),
        monthly_price: plan.price,
        trial_days: plan.trial_days
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/admin/riads', formData);
      toast.success('Riad créé avec succès');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouveau Riad</DialogTitle>
          <DialogDescription>
            Créer un nouvel établissement avec son administrateur et son abonnement
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Riad Info */}
          <div className="space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Informations de l'établissement
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nom *</Label>
                <Input
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Riad El Fenn"
                  required
                />
              </div>
              
              <div>
                <Label>Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={e => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="riad-el-fenn"
                />
                <p className="text-xs text-muted-foreground mt-1">Généré automatiquement si vide</p>
              </div>
              
              <div>
                <Label>Sous-domaine</Label>
                <Input
                  value={formData.subdomain}
                  onChange={e => setFormData({ ...formData, subdomain: e.target.value })}
                  placeholder="elfenn"
                />
              </div>
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@riad.ma"
                />
              </div>
              
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+212 5 24 XX XX XX"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Adresse</Label>
                <Input
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Derb ..., Médina"
                />
              </div>
              
              <div>
                <Label>Ville</Label>
                <Input
                  value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Marrakech"
                />
              </div>
              
              <div>
                <Label>Devise</Label>
                <Select 
                  value={formData.currency} 
                  onValueChange={v => setFormData({ ...formData, currency: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MAD">MAD - Dirham</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="USD">USD - Dollar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Admin User */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium flex items-center gap-2">
              <Users className="w-4 h-4" />
              Administrateur principal
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Email admin *</Label>
                <Input
                  type="email"
                  value={formData.admin_email}
                  onChange={e => setFormData({ ...formData, admin_email: e.target.value })}
                  placeholder="admin@riad.ma"
                  required
                />
              </div>
              
              <div>
                <Label>Prénom</Label>
                <Input
                  value={formData.admin_first_name}
                  onChange={e => setFormData({ ...formData, admin_first_name: e.target.value })}
                  placeholder="Mohammed"
                />
              </div>
              
              <div>
                <Label>Nom</Label>
                <Input
                  value={formData.admin_last_name}
                  onChange={e => setFormData({ ...formData, admin_last_name: e.target.value })}
                  placeholder="El Amrani"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Mot de passe *</Label>
                <Input
                  type="password"
                  value={formData.admin_password}
                  onChange={e => setFormData({ ...formData, admin_password: e.target.value })}
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Abonnement
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan</Label>
                <Select 
                  value={formData.plan_id?.toString() || ''} 
                  onValueChange={handlePlanChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map(plan => (
                      <SelectItem key={plan.id} value={plan.id.toString()}>
                        {plan.name} ({plan.price} {plan.currency}/
                        {plan.periodicity === 'MONTHLY' ? 'mois' : plan.periodicity === 'YEARLY' ? 'an' : plan.periodicity.toLowerCase()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Statut</Label>
                <Select 
                  value={formData.subscription_status} 
                  onValueChange={v => setFormData({ ...formData, subscription_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRIAL">Essai</SelectItem>
                    <SelectItem value="ACTIVE">Actif</SelectItem>
                    <SelectItem value="LIFETIME">Lifetime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Prix mensuel (MAD)</Label>
                <Input
                  type="number"
                  value={formData.monthly_price}
                  onChange={e => setFormData({ ...formData, monthly_price: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Jours d'essai</Label>
                <Input
                  type="number"
                  value={formData.trial_days}
                  onChange={e => setFormData({ ...formData, trial_days: parseInt(e.target.value) || 0 })}
                  placeholder="14"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer le riad
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============ RIAD DETAIL DIALOG ============
const RiadDetailDialog = ({ riadId, open, onOpenChange, mode = 'view', onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [riad, setRiad] = useState(null);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState({});
  const [stats, setStats] = useState({});
  const [editMode, setEditMode] = useState(mode === 'edit');

  useEffect(() => {
    if (open && riadId) {
      fetchRiad();
    }
  }, [open, riadId]);

  const fetchRiad = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/riads/${riadId}`);
      setRiad(data.riad);
      setUsers(data.users);
      setModules(data.modules);
      setStats(data.stats);
    } catch (error) {
      toast.error('Erreur lors du chargement');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/riads/${riadId}`, riad);
      toast.success('Riad mis à jour');
      setEditMode(false);
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {riad?.name || 'Chargement...'}
          </DialogTitle>
          {riad && (
            <DialogDescription>
              {riad.slug} • {riad.currency} • {statusConfig[riad.status]?.label}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="info" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="info">Infos</TabsTrigger>
              <TabsTrigger value="subscription">Abonnement</TabsTrigger>
              <TabsTrigger value="users">Utilisateurs ({users.length})</TabsTrigger>
              <TabsTrigger value="modules">Modules</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4 mt-4">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold">{stats.reservations}</div>
                    <p className="text-xs text-muted-foreground">Réservations</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold">{stats.guests}</div>
                    <p className="text-xs text-muted-foreground">Clients</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold">{stats.products}</div>
                    <p className="text-xs text-muted-foreground">Produits</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <div className="text-2xl font-bold">{stats.employees}</div>
                    <p className="text-xs text-muted-foreground">Employés</p>
                  </CardContent>
                </Card>
              </div>

              {/* Basic Info */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Informations générales</h4>
                  {!editMode && (
                    <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Modifier
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom</Label>
                    <Input
                      value={riad?.name || ''}
                      onChange={e => setRiad({ ...riad, name: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <Label>Slug</Label>
                    <Input
                      value={riad?.slug || ''}
                      onChange={e => setRiad({ ...riad, slug: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={riad?.email || ''}
                      onChange={e => setRiad({ ...riad, email: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input
                      value={riad?.phone || ''}
                      onChange={e => setRiad({ ...riad, phone: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Adresse</Label>
                    <Input
                      value={riad?.address || ''}
                      onChange={e => setRiad({ ...riad, address: e.target.value })}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <Label>Statut</Label>
                    <Select 
                      value={riad?.status} 
                      onValueChange={v => setRiad({ ...riad, status: v })}
                      disabled={!editMode}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Actif</SelectItem>
                        <SelectItem value="SUSPENDED">Suspendu</SelectItem>
                        <SelectItem value="DISABLED">Désactivé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Setup</Label>
                    <Badge className={setupStateConfig[riad?.setup_state]?.color}>
                      {setupStateConfig[riad?.setup_state]?.label}
                    </Badge>
                  </div>
                </div>

                {editMode && (
                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => setEditMode(false)}>
                      Annuler
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                      {saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                      Enregistrer
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="subscription" className="space-y-4 mt-4">
              {riad?.subscription && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <Label className="text-muted-foreground">Plan</Label>
                        <p className="text-xl font-bold">{riad.subscription.plan}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <Label className="text-muted-foreground">Statut</Label>
                        <Badge className={statusConfig[riad.subscription.status]?.color}>
                          {statusConfig[riad.subscription.status]?.label}
                        </Badge>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <Label className="text-muted-foreground">Prix mensuel</Label>
                        <p className="text-xl font-bold">
                          {riad.subscription.monthly_price?.toLocaleString()} {riad.subscription.currency || 'MAD'}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <Label className="text-muted-foreground">Date fin</Label>
                        <p className="text-xl font-bold">
                          {riad.subscription.end_date 
                            ? format(new Date(riad.subscription.end_date), 'dd/MM/yyyy')
                            : 'Sans fin'
                          }
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="users" className="space-y-4 mt-4">
              <div className="space-y-3">
                {users.map(access => (
                  <div key={access.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {access.user?.first_name} {access.user?.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground">{access.user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{access.role}</Badge>
                      {access.is_owner && <Badge>Propriétaire</Badge>}
                      {!access.is_active && <Badge variant="destructive">Inactif</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="modules" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(modules).map(([module, enabled]) => (
                  <div 
                    key={module}
                    className={`p-3 rounded-lg border ${enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-center gap-2">
                      {enabled ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-400" />
                      )}
                      <span className={enabled ? 'font-medium' : 'text-muted-foreground'}>
                        {module}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============ PLATFORM SETTINGS TAB ============
const PlatformSettingsTab = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('vendor');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings');
      setSettings(data.flat || {});
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', { settings });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const sections = {
    vendor: {
      title: 'Informations Vendeur',
      description: 'Ces informations apparaissent sur les factures SaaS',
      icon: Building,
      fields: [
        { key: 'vendor.name', label: 'Nom de la société', placeholder: 'My Riad Manager' },
        { key: 'vendor.address', label: 'Adresse', placeholder: '123 Rue du Commerce' },
        { key: 'vendor.city', label: 'Ville', placeholder: 'Marrakech' },
        { key: 'vendor.country', label: 'Pays', placeholder: 'Maroc' },
        { key: 'vendor.phone', label: 'Téléphone', placeholder: '+212 5XX XXX XXX' },
        { key: 'vendor.email', label: 'Email facturation', placeholder: 'facturation@example.com' },
        { key: 'vendor.website', label: 'Site web', placeholder: 'https://example.com' }
      ]
    },
    legal: {
      title: 'Informations Légales',
      description: 'Identifiants légaux de votre entreprise',
      icon: Shield,
      fields: [
        { key: 'legal.ice', label: 'ICE', placeholder: 'Identifiant Commun de l\'Entreprise' },
        { key: 'legal.rc', label: 'RC', placeholder: 'Registre de Commerce' },
        { key: 'legal.if', label: 'IF', placeholder: 'Identifiant Fiscal' },
        { key: 'legal.cnss', label: 'CNSS', placeholder: 'Numéro CNSS' },
        { key: 'legal.patent', label: 'Patente', placeholder: 'Numéro de Patente' }
      ]
    },
    billing: {
      title: 'Paramètres de Facturation',
      description: 'Configuration par défaut des factures',
      icon: CreditCard,
      fields: [
        { key: 'billing.vat_rate', label: 'Taux TVA (%)', placeholder: '20', type: 'number' },
        { key: 'billing.currency', label: 'Devise', placeholder: 'MAD' },
        { key: 'billing.payment_terms', label: 'Délai de paiement (jours)', placeholder: '30', type: 'number' },
        { key: 'billing.bank_name', label: 'Banque', placeholder: 'Nom de la banque' },
        { key: 'billing.bank_iban', label: 'IBAN', placeholder: 'MA00 0000 0000 0000 0000 0000' },
        { key: 'billing.bank_bic', label: 'BIC', placeholder: 'XXXXXXXX' }
      ]
    },
    invoice: {
      title: 'Notes de Facture',
      description: 'Textes apparaissant sur les factures',
      icon: FileText,
      fields: [
        { key: 'invoice.footer_note', label: 'Note de bas de page', placeholder: 'Merci de votre confiance', multiline: true },
        { key: 'invoice.payment_instructions', label: 'Instructions de paiement', placeholder: 'Virement bancaire sous 30 jours', multiline: true }
      ]
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar Navigation */}
      <div className="space-y-2">
        {Object.entries(sections).map(([key, section]) => {
          const Icon = section.icon;
          return (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                activeSection === key 
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                  : 'hover:bg-stone-50 text-stone-600'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium text-sm">{section.title}</span>
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="lg:col-span-3">
        <Card className="border-stone-200/60 shadow-sm">
          <CardHeader className="border-b border-stone-100">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  {React.createElement(sections[activeSection].icon, { className: "w-5 h-5 text-emerald-600" })}
                  {sections[activeSection].title}
                </CardTitle>
                <CardDescription>{sections[activeSection].description}</CardDescription>
              </div>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                Enregistrer
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sections[activeSection].fields.map(field => (
                <div key={field.key} className={field.multiline ? 'md:col-span-2' : ''}>
                  <Label className="text-sm font-medium text-stone-700">{field.label}</Label>
                  {field.multiline ? (
                    <Textarea
                      value={settings[field.key] || ''}
                      onChange={e => updateSetting(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="mt-1.5"
                      rows={3}
                    />
                  ) : (
                    <Input
                      type={field.type || 'text'}
                      value={settings[field.key] || ''}
                      onChange={e => updateSetting(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className="mt-1.5"
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Preview Card */}
        {activeSection === 'vendor' && (
          <Card className="border-stone-200/60 shadow-sm mt-6">
            <CardHeader>
              <CardTitle className="text-sm text-stone-500">Aperçu Facture</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-stone-50 rounded-lg">
                <p className="font-bold text-lg text-stone-800">{settings['vendor.name'] || 'Nom de la société'}</p>
                <p className="text-sm text-stone-600">
                  {settings['vendor.address'] && `${settings['vendor.address']}, `}
                  {settings['vendor.city'] || 'Ville'}, {settings['vendor.country'] || 'Pays'}
                </p>
                {settings['vendor.phone'] && <p className="text-sm text-stone-500">Tél: {settings['vendor.phone']}</p>}
                {settings['vendor.email'] && <p className="text-sm text-stone-500">{settings['vendor.email']}</p>}
                <div className="mt-3 pt-3 border-t border-stone-200 text-xs text-stone-400">
                  {settings['legal.ice'] && <span className="mr-3">ICE: {settings['legal.ice']}</span>}
                  {settings['legal.rc'] && <span className="mr-3">RC: {settings['legal.rc']}</span>}
                  {settings['legal.if'] && <span>IF: {settings['legal.if']}</span>}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// ============ MAIN PAGE ============
export default function SuperAdminPage({ initialTab }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab || 'dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [selectedRiad, setSelectedRiad] = useState(null);
  const [riadDialogMode, setRiadDialogMode] = useState('view');

  const fetchDashboard = async () => {
    setDashboardLoading(true);
    try {
      const { data } = await api.get('/admin/dashboard');
      setDashboardData(data);
    } catch (error) {
      toast.error('Erreur lors du chargement du dashboard');
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const handleRiadSelect = (riadId, mode = 'view') => {
    setSelectedRiad(riadId);
    setRiadDialogMode(mode);
  };

  if (!user?.is_super_admin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md border-stone-200/60">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Accès restreint</h2>
            <p className="text-stone-500">
              Cette page est réservée aux super administrateurs de la plateforme.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/20" data-testid="super-admin-page">
      {/* Premium Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-stone-200/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={BRANDING.logo} alt={BRANDING.name} className="w-10 h-10" />
              <div>
                <h1 className="text-xl font-bold text-slate-800">{BRANDING.name}</h1>
                <p className="text-sm text-slate-500">Administration</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={fetchDashboard} className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Actualiser
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/60 backdrop-blur border border-stone-200/60 p-1 rounded-xl shadow-sm mb-8">
            <TabsTrigger value="dashboard" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <TrendingUp className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="riads" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Building2 className="w-4 h-4" />
              Riads
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Users className="w-4 h-4" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="plans" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Package className="w-4 h-4" />
              Plans
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <CreditCard className="w-4 h-4" />
              Abonnements
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Settings className="w-4 h-4" />
              Paramètres
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab 
              data={dashboardData} 
              loading={dashboardLoading} 
              onRefresh={fetchDashboard}
            />
          </TabsContent>

          <TabsContent value="riads">
            <RiadsTab onRiadSelect={handleRiadSelect} />
          </TabsContent>

          <TabsContent value="users">
            <PlatformUsersTab />
          </TabsContent>

          <TabsContent value="plans">
            <PlansTab />
          </TabsContent>

          <TabsContent value="subscriptions">
            <SubscriptionsTab />
          </TabsContent>

          <TabsContent value="settings">
            <PlatformSettingsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Riad Detail Dialog */}
      <RiadDetailDialog
        riadId={selectedRiad}
        open={!!selectedRiad}
        onOpenChange={(open) => !open && setSelectedRiad(null)}
        mode={riadDialogMode}
        onUpdate={fetchDashboard}
      />
    </div>
  );
}

// ============ PLATFORM USERS TAB ============
const PlatformUsersTab = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filter === 'super_admin') params.append('is_super_admin', 'true');
      if (filter === 'active') params.append('status', 'active');
      if (filter === 'inactive') params.append('status', 'inactive');
      
      const { data } = await api.get(`/admin/users?${params}`);
      setUsers(data.users);
      setStats(data.stats);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuperAdmin = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/users/super-admin', formData);
      toast.success('Super administrateur créé');
      setShowCreateDialog(false);
      setFormData({ email: '', password: '', first_name: '', last_name: '', phone: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/admin/users/${user.id}`, { active: !user.active });
      toast.success(user.active ? 'Utilisateur désactivé' : 'Utilisateur activé');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleToggleSuperAdmin = async (user) => {
    if (user.is_super_admin) {
      if (!confirm('Retirer le statut super admin à cet utilisateur ?')) return;
    } else {
      if (!confirm('Donner le statut super admin à cet utilisateur ?')) return;
    }
    try {
      await api.put(`/admin/users/${user.id}`, { is_super_admin: !user.is_super_admin });
      toast.success(user.is_super_admin ? 'Statut super admin retiré' : 'Statut super admin accordé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Minimum 6 caractères');
      return;
    }
    try {
      await api.put(`/admin/users/${showPasswordDialog}/reset-password`, { new_password: newPassword });
      toast.success('Mot de passe réinitialisé');
      setShowPasswordDialog(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (user) => {
    if (!confirm(`Supprimer définitivement ${user.email} ?`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const filteredUsers = users.filter(user =>
    !search || 
    `${user.first_name} ${user.last_name} ${user.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{stats.total || 0}</div>
            <p className="text-xs text-muted-foreground">Total utilisateurs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.super_admins || 0}</div>
            <p className="text-xs text-muted-foreground">Super Admins</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.active || 0}</div>
            <p className="text-xs text-muted-foreground">Actifs</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-gray-400">{stats.inactive || 0}</div>
            <p className="text-xs text-muted-foreground">Inactifs</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un utilisateur..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
            className="pl-10"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les utilisateurs</SelectItem>
            <SelectItem value="super_admin">Super Admins uniquement</SelectItem>
            <SelectItem value="active">Actifs</SelectItem>
            <SelectItem value="inactive">Inactifs</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Super Admin
        </Button>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-2">
          {filteredUsers.map(user => (
            <Card key={user.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      user.is_super_admin ? 'bg-purple-100 text-purple-600' : 'bg-primary/10 text-primary'
                    }`}>
                      {user.is_super_admin ? (
                        <Shield className="w-5 h-5" />
                      ) : (
                        <span className="font-semibold">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{user.first_name} {user.last_name}</span>
                        {user.is_super_admin && (
                          <Badge className="bg-purple-100 text-purple-700">Super Admin</Badge>
                        )}
                        {!user.active && (
                          <Badge variant="secondary">Inactif</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                      {user.riad_accesses?.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {user.riad_accesses.slice(0, 3).map(access => (
                            <Badge key={access.id} variant="outline" className="text-xs">
                              {access.riad?.name || `Riad #${access.riad_id}`}
                            </Badge>
                          ))}
                          {user.riad_accesses.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{user.riad_accesses.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                          {user.active ? (
                            <>
                              <Pause className="w-4 h-4 mr-2" />
                              Désactiver
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-2" />
                              Activer
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleSuperAdmin(user)}>
                          <Shield className="w-4 h-4 mr-2" />
                          {user.is_super_admin ? 'Retirer Super Admin' : 'Rendre Super Admin'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setShowPasswordDialog(user.id)}>
                          <Settings className="w-4 h-4 mr-2" />
                          Réinitialiser mot de passe
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(user)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              Aucun utilisateur trouvé
            </div>
          )}
        </div>
      )}

      {/* Create Super Admin Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau Super Administrateur</DialogTitle>
            <DialogDescription>
              Créer un utilisateur avec accès complet à la plateforme
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSuperAdmin} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={formData.first_name}
                  onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={formData.last_name}
                  onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Mot de passe *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={creating}>
                {creating && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showPasswordDialog} onOpenChange={(open) => !open && setShowPasswordDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nouveau mot de passe *</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                minLength={6}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(null)}>
              Annuler
            </Button>
            <Button onClick={handleResetPassword}>
              Réinitialiser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ SUBSCRIPTIONS TAB ============
const SubscriptionsTab = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [plans, setPlans] = useState([]);
  const [changePlanDialog, setChangePlanDialog] = useState({ open: false, subscription: null });

  useEffect(() => {
    fetchSubscriptions();
    fetchPlans();
  }, [statusFilter]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const { data } = await api.get(`/admin/subscriptions?${params}`);
      setSubscriptions(data.subscriptions);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/admin/plans?status=ACTIVE');
      setPlans(data.plans);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const handleExtend = async (subscriptionId, months) => {
    try {
      await api.post(`/admin/subscriptions/${subscriptionId}/extend`, { months });
      toast.success('Abonnement prolongé');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleChangePlan = async (subscriptionId, planId) => {
    try {
      await api.post(`/admin/subscriptions/${subscriptionId}/change-plan`, { plan_id: planId });
      toast.success('Plan changé avec succès');
      setChangePlanDialog({ open: false, subscription: null });
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="ACTIVE">Actifs</SelectItem>
            <SelectItem value="TRIAL">Essai</SelectItem>
            <SelectItem value="EXPIRED">Expirés</SelectItem>
            <SelectItem value="SUSPENDED">Suspendus</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-3">
          {subscriptions.map(sub => {
            const status = statusConfig[sub.status] || statusConfig.ACTIVE;
            const StatusIcon = status.icon;
            const planDetails = sub.planDetails;
            
            return (
              <Card key={sub.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{sub.riad?.name || 'Riad #' + sub.riad_id}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Badge variant="outline" style={{ 
                            borderColor: planDetails?.color || '#3B82F6',
                            color: planDetails?.color || '#3B82F6'
                          }}>
                            {planDetails?.name || sub.plan}
                          </Badge>
                          <span>•</span>
                          <span>{sub.monthly_price?.toLocaleString()} {sub.currency || 'MAD'}/mois</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <Badge className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                        {sub.end_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Expire le {format(new Date(sub.end_date), 'dd/MM/yyyy')}
                          </p>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setChangePlanDialog({ open: true, subscription: sub })}>
                            <Package className="w-4 h-4 mr-2" />
                            Changer de plan
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleExtend(sub.id, 1)}>
                            <Calendar className="w-4 h-4 mr-2" />
                            +1 mois
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExtend(sub.id, 3)}>
                            <Calendar className="w-4 h-4 mr-2" />
                            +3 mois
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleExtend(sub.id, 12)}>
                            <Calendar className="w-4 h-4 mr-2" />
                            +1 an
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Change Plan Dialog */}
      <Dialog open={changePlanDialog.open} onOpenChange={(open) => !open && setChangePlanDialog({ open: false, subscription: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer de plan</DialogTitle>
            <DialogDescription>
              Sélectionnez le nouveau plan pour {changePlanDialog.subscription?.riad?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {plans.map(plan => {
              const isCurrentPlan = changePlanDialog.subscription?.plan_id === plan.id;
              return (
                <Card 
                  key={plan.id}
                  className={`cursor-pointer transition-all ${isCurrentPlan ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
                  onClick={() => !isCurrentPlan && handleChangePlan(changePlanDialog.subscription?.id, plan.id)}
                  style={{ borderTopColor: plan.color, borderTopWidth: '3px' }}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{plan.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {plan.price.toLocaleString()} {plan.currency}/{plan.periodicity === 'MONTHLY' ? 'mois' : 'an'}
                        </p>
                      </div>
                      {isCurrentPlan && (
                        <Badge>Actuel</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePlanDialog({ open: false, subscription: null })}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============ PLANS TAB ============
const PlansTab = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableModules, setAvailableModules] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchModules();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/plans?include_stats=true');
      setPlans(data.plans);
    } catch (error) {
      toast.error('Erreur lors du chargement des plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const { data } = await api.get('/admin/plans/modules');
      setAvailableModules(data.modules);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const handleDelete = async (planId) => {
    if (!confirm('Supprimer ce plan ?')) return;
    try {
      await api.delete(`/admin/plans/${planId}`);
      toast.success('Plan supprimé');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDuplicate = async (planId) => {
    const code = prompt('Code du nouveau plan (ex: premium_v2):');
    if (!code) return;
    try {
      await api.post(`/admin/plans/${planId}/duplicate`, { new_code: code });
      toast.success('Plan dupliqué');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const getPlanIcon = (icon) => {
    switch(icon) {
      case 'Zap': return Zap;
      case 'Building': return Building;
      case 'Crown': return Crown;
      case 'Building2': return Building2;
      default: return Package;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold">Plans tarifaires</h2>
          <p className="text-sm text-muted-foreground">Gérez les plans et leurs modules autorisés</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Plan
        </Button>
      </div>

      {/* Plans Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map(plan => {
            const PlanIcon = getPlanIcon(plan.icon);
            const activeModules = Object.values(plan.allowed_modules || {}).filter(v => v).length;
            const totalModules = Object.keys(plan.allowed_modules || {}).length;
            
            return (
              <Card 
                key={plan.id} 
                className={`relative overflow-hidden ${plan.status !== 'ACTIVE' ? 'opacity-60' : ''}`}
                style={{ borderTopColor: plan.color, borderTopWidth: '3px' }}
              >
                {plan.badge && (
                  <div 
                    className="absolute top-2 right-2 px-2 py-0.5 text-xs font-medium rounded-full text-white"
                    style={{ backgroundColor: plan.color }}
                  >
                    {plan.badge}
                  </div>
                )}
                
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${plan.color}20` }}
                    >
                      <PlanIcon className="w-5 h-5" style={{ color: plan.color }} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{plan.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">{plan.code}</p>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-2xl font-bold">{plan.price.toLocaleString()}</span>
                    <span className="text-sm text-muted-foreground ml-1">
                      {plan.currency}/{plan.periodicity === 'MONTHLY' ? 'mois' : plan.periodicity === 'YEARLY' ? 'an' : plan.periodicity.toLowerCase()}
                    </span>
                  </div>
                  
                  <p className="text-xs text-muted-foreground line-clamp-2">{plan.description}</p>
                  
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {activeModules}/{totalModules} modules
                    </span>
                    <Badge variant={plan.status === 'ACTIVE' ? 'default' : 'secondary'}>
                      {plan.status}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs pt-2 border-t">
                    <span className="text-muted-foreground">
                      {plan.subscription_count || 0} abonnés
                    </span>
                    {plan.is_default && (
                      <Badge variant="outline" className="text-xs">Par défaut</Badge>
                    )}
                  </div>
                  
                  <div className="flex gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => setEditingPlan(plan)}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Modifier
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDuplicate(plan.id)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Dupliquer
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(plan.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <PlanDialog 
        open={showCreateDialog || !!editingPlan}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setEditingPlan(null);
          }
        }}
        plan={editingPlan}
        modules={availableModules}
        onSuccess={() => {
          setShowCreateDialog(false);
          setEditingPlan(null);
          fetchPlans();
        }}
      />
    </div>
  );
};

// ============ PLAN DIALOG (Create/Edit) ============
const PlanDialog = ({ open, onOpenChange, plan, modules, onSuccess }) => {
  const isEdit = !!plan;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    price: 0,
    currency: 'MAD',
    periodicity: 'MONTHLY',
    status: 'ACTIVE',
    is_public: true,
    is_default: false,
    trial_days: 14,
    color: '#3B82F6',
    icon: 'Package',
    badge: '',
    allowed_modules: {},
    limits: {
      max_users: 5,
      max_reservations_month: 100,
      max_guests: 500,
      max_products: 50,
      max_employees: 10,
      max_storage_mb: 500,
      max_backups: 5
    }
  });

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name || '',
        code: plan.code || '',
        description: plan.description || '',
        price: plan.price || 0,
        currency: plan.currency || 'MAD',
        periodicity: plan.periodicity || 'MONTHLY',
        status: plan.status || 'ACTIVE',
        is_public: plan.is_public !== false,
        is_default: plan.is_default || false,
        trial_days: plan.trial_days || 14,
        color: plan.color || '#3B82F6',
        icon: plan.icon || 'Package',
        badge: plan.badge || '',
        allowed_modules: plan.allowed_modules || {},
        limits: plan.limits || {}
      });
    } else {
      // Reset for new plan
      const defaultModules = {};
      modules.forEach(m => { defaultModules[m.code] = true; });
      setFormData(prev => ({
        ...prev,
        name: '',
        code: '',
        description: '',
        allowed_modules: defaultModules
      }));
    }
  }, [plan, modules]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await api.put(`/admin/plans/${plan.id}`, formData);
        toast.success('Plan mis à jour');
      } else {
        await api.post('/admin/plans', formData);
        toast.success('Plan créé');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (code) => {
    setFormData(prev => ({
      ...prev,
      allowed_modules: {
        ...prev.allowed_modules,
        [code]: !prev.allowed_modules[code]
      }
    }));
  };

  const updateLimit = (key, value) => {
    setFormData(prev => ({
      ...prev,
      limits: {
        ...prev.limits,
        [key]: parseInt(value) || 0
      }
    }));
  };

  const enableAllModules = () => {
    const all = {};
    modules.forEach(m => { all[m.code] = true; });
    setFormData(prev => ({ ...prev, allowed_modules: all }));
  };

  const enableMinModules = () => {
    const min = {};
    modules.forEach(m => { min[m.code] = m.required || false; });
    min['reservations'] = true;
    min['calendar'] = true;
    setFormData(prev => ({ ...prev, allowed_modules: min }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le plan' : 'Nouveau plan'}</DialogTitle>
          <DialogDescription>
            Configurez les détails, modules autorisés et limites du plan
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Premium"
                required
              />
            </div>
            <div>
              <Label>Code *</Label>
              <Input
                value={formData.code}
                onChange={e => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                placeholder="premium"
                required
                disabled={isEdit}
              />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description du plan..."
                rows={2}
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Tarification</h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Prix</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Devise</Label>
                <Select value={formData.currency} onValueChange={v => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MAD">MAD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Périodicité</Label>
                <Select value={formData.periodicity} onValueChange={v => setFormData({ ...formData, periodicity: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MONTHLY">Mensuel</SelectItem>
                    <SelectItem value="QUARTERLY">Trimestriel</SelectItem>
                    <SelectItem value="YEARLY">Annuel</SelectItem>
                    <SelectItem value="LIFETIME">Lifetime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Essai (jours)</Label>
                <Input
                  type="number"
                  value={formData.trial_days}
                  onChange={e => setFormData({ ...formData, trial_days: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>

          {/* Status & Display */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Affichage</h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Statut</Label>
                <Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Actif</SelectItem>
                    <SelectItem value="INACTIVE">Inactif</SelectItem>
                    <SelectItem value="DEPRECATED">Deprecated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Couleur</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={e => setFormData({ ...formData, color: e.target.value })}
                    className="w-12 h-9 p-1"
                  />
                  <Input
                    value={formData.color}
                    onChange={e => setFormData({ ...formData, color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label>Badge</Label>
                <Input
                  value={formData.badge}
                  onChange={e => setFormData({ ...formData, badge: e.target.value })}
                  placeholder="Populaire"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Public</Label>
                  <Switch
                    checked={formData.is_public}
                    onCheckedChange={v => setFormData({ ...formData, is_public: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Par défaut</Label>
                  <Switch
                    checked={formData.is_default}
                    onCheckedChange={v => setFormData({ ...formData, is_default: v })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Modules */}
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Modules autorisés</h4>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={enableAllModules}>
                  Tout activer
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={enableMinModules}>
                  Minimum
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {modules.map(module => (
                <div 
                  key={module.code}
                  className={`flex items-center justify-between p-2 rounded border cursor-pointer transition-colors ${
                    formData.allowed_modules[module.code] 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                  onClick={() => toggleModule(module.code)}
                >
                  <div className="flex items-center gap-2">
                    {formData.allowed_modules[module.code] ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                    <span className={`text-sm ${formData.allowed_modules[module.code] ? 'font-medium' : 'text-muted-foreground'}`}>
                      {module.name}
                    </span>
                  </div>
                  {module.required && (
                    <Badge variant="outline" className="text-xs">Requis</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Limits */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">Limites (0 = illimité)</h4>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Max utilisateurs</Label>
                <Input
                  type="number"
                  value={formData.limits.max_users || 0}
                  onChange={e => updateLimit('max_users', e.target.value)}
                />
              </div>
              <div>
                <Label>Réservations/mois</Label>
                <Input
                  type="number"
                  value={formData.limits.max_reservations_month || 0}
                  onChange={e => updateLimit('max_reservations_month', e.target.value)}
                />
              </div>
              <div>
                <Label>Max clients</Label>
                <Input
                  type="number"
                  value={formData.limits.max_guests || 0}
                  onChange={e => updateLimit('max_guests', e.target.value)}
                />
              </div>
              <div>
                <Label>Max produits</Label>
                <Input
                  type="number"
                  value={formData.limits.max_products || 0}
                  onChange={e => updateLimit('max_products', e.target.value)}
                />
              </div>
              <div>
                <Label>Max employés</Label>
                <Input
                  type="number"
                  value={formData.limits.max_employees || 0}
                  onChange={e => updateLimit('max_employees', e.target.value)}
                />
              </div>
              <div>
                <Label>Stockage (Mo)</Label>
                <Input
                  type="number"
                  value={formData.limits.max_storage_mb || 0}
                  onChange={e => updateLimit('max_storage_mb', e.target.value)}
                />
              </div>
              <div>
                <Label>Max sauvegardes</Label>
                <Input
                  type="number"
                  value={formData.limits.max_backups || 0}
                  onChange={e => updateLimit('max_backups', e.target.value)}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              {isEdit ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
